import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Horisontal swipe mellom sider.
 *
 * Bare hovedfeltet flytter seg — toppstripe og bunnstripe står. Glasskanten
 * ligger inni hver side og glir med.
 *
 * Bevegelsen følger fingeren og lander med en fjær. Vertikal blaing i loggen
 * vinner: aksen låses ved første tydelige bevegelse, og er den loddrett,
 * slipper vi gesten helt. Konflikten er reell og må prøves på enhet — se
 * docs/12-ui-feltapp.md.
 */
export function Pager({
  index, count, onIndexChange, onPageCommit, children,
}: {
  index: number;
  count: number;
  onIndexChange(i: number): void;
  /**
   * Kalles naar en sidegest begynner.
   *
   * Skrives det, skal posten lagres og tastaturet legges vekk foer sida
   * bytter — regi skal kunne gaa fra logg til hurtiglogg med én sveip,
   * uansett hvilken tilstand loggen staar i.
   */
  /**
   * Kalles naar et sidebytte er bestemt, rett foer indeksen endres.
   *
   * MAA ikke kalles naar gesten starter. Blurringen legger vekk tastaturet,
   * flata endrer hoeyde under fingeren, og pekeren avlyses — da ble foerste
   * sveip bare en tastaturlukking, og man maatte sveipe to ganger.
   */
  onPageCommit?: () => void;
  children: readonly ReactNode[];
}): JSX.Element {
  const viewport = useRef<HTMLDivElement | null>(null);
  const [dragDx, setDragDx] = useState<number | null>(null);
  const gesture = useRef<{
    id: number; x: number; y: number; dx: number; axis: "x" | "y" | null; width: number;
  } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const width = viewport.current?.getBoundingClientRect().width ?? 1;
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, axis: null, width };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const g = gesture.current;
    if (g === null || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;

    if (g.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      g.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (g.axis === "y") { gesture.current = null; return; }
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ingen aktiv peker */ }
    }

    g.dx = dx;

    // Motstand utenfor endene i stedet for en hard vegg.
    const atStart = index === 0 && dx > 0;
    const atEnd = index >= count - 1 && dx < 0;
    setDragDx(atStart || atEnd ? dx * 0.3 : dx);
  }, [index, count]);

  /*
   * Landingen bruker siste kjente dx fra bevegelsen, ikke hendelsens egen
   * clientX.
   *
   * finish var ogsaa pointercancel-handler, og en avbrutt peker kommer ofte
   * med clientX 0. Da ble dx hele startposisjonen i minus — lest som et
   * langt drag mot venstre — og appen byttet side av seg selv.
   */
  const finish = useCallback((): void => {
    const g = gesture.current;
    gesture.current = null;
    if (g === null || g.axis !== "x") { setDragDx(null); return; }

    const dx = g.dx;
    const threshold = Math.min(56, g.width * 0.18);
    setDragDx(null);
    if (Math.abs(dx) <= threshold) return;
    const next = dx < 0 ? Math.min(count - 1, index + 1) : Math.max(0, index - 1);
    if (next === index) return;
    /*
     * Foerst naa. Skrives det, lagres posten og tastaturet legges vekk — men
     * etter at gesten er sluppet, saa den ikke avbrytes av at flata endrer
     * hoeyde midt i. En sveip som ikke naadde terskelen lar tastaturet staa:
     * da ville regi bli vekk fra teksten uten aa ha bedt om det.
     */
    onPageCommit?.();
    onIndexChange(next);
  }, [index, count, onIndexChange, onPageCommit]);

  /*
   * Hindrer WebKit i aa overta et horisontalt drag som alt er et sidebytte.
   *
   * Starter draget paa et fokusert skrivefelt, tolker iOS det som flytting av
   * markoeren eller markering av tekst, og sender `pointercancel` til oss. Da
   * doer sveipen foer den rekker aa bli et sidebytte, og man maatte legge vekk
   * tastaturet foerst for aa kunne bla.
   *
   * Lytteren maa settes med addEventListener: React knytter `touchmove` passivt
   * til rota, og en passiv lytter kan ikke kalle preventDefault. Og den avlyser
   * bare naar aksen alt er avgjort til x — vertikal blaing i loggen og markoeren
   * i teksten skal fortsatt virke som foer.
   */
  useEffect(() => {
    const el = viewport.current;
    if (el === null) return;
    const paa = (e: TouchEvent): void => {
      if (gesture.current?.axis === "x") e.preventDefault();
    };
    el.addEventListener("touchmove", paa, { passive: false });
    return () => el.removeEventListener("touchmove", paa);
  }, []);

  // Er en side skrudd av mens man står på den, faller indeksen innenfor.
  useEffect(() => {
    if (index > count - 1) onIndexChange(Math.max(0, count - 1));
  }, [index, count, onIndexChange]);

  const dragging = dragDx !== null;
  const offset = dragging
    ? `calc(${-index * 100}% + ${dragDx}px)`
    : `${-index * 100}%`;

  return (
    <div
      className="pager"
      ref={viewport}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={() => { gesture.current = null; setDragDx(null); }}
    >
      <div
        className={`pager__track ${dragging ? "pager__track--dragging" : "pager__track--settling"}`}
        style={{ transform: `translate3d(${offset}, 0, 0)` }}
      >
        {children.map((child, i) => (
          <div className="pager__page" key={i}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}
